import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { updateProfile } from './authSlice';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

export interface KYCDocument {
  type: 'passport' | 'drivers_license' | 'national_id';
  documentId: string;
  frontImage: string;
  backImage: string;
  verified: boolean;
  verifiedAt?: string;
  expirationDate?: string | null;
}

interface KYCState {
  status: 'none' | 'pending' | 'in_progress' | 'approved' | 'rejected';
  citizenStatus: boolean;
  documents: KYCDocument[];
  hasBiometrics: boolean;
  submittedAt?: string;
  approvedAt?: string;
  rejectionReason?: string;
  expirationDate?: string | null;
  expired: boolean;
  requiresKYC: boolean;
  isSubmitting: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: KYCState = {
  status: 'none',
  citizenStatus: false,
  documents: [],
  hasBiometrics: false,
  expirationDate: null,
  expired: false,
  requiresKYC: true,
  isSubmitting: false,
  isLoading: false,
  error: null,
};

export const fetchKYCStatus = createAsyncThunk(
  'kyc/fetchStatus',
  async (_, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.get(
      `${API_BASE_URL}/kyc/status`,
      { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
    );
    return response.data.data;
  }
);

export const submitKYC = createAsyncThunk(
  'kyc/submit',
  async (_, { getState, dispatch }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(
      `${API_BASE_URL}/kyc/submit`,
      {},
      {
        headers: {
          Authorization: `Bearer ${state.auth.accessToken}`,
        },
      }
    );
    
    // Refresh user data to get updated KYC status
    await dispatch(updateProfile({}));
    
    return response.data.data;
  }
);

const kycSlice = createSlice({
  name: 'kyc',
  initialState,
  reducers: {
    updateDocument: (state, action) => {
      const { type, side, url, expirationDate } = action.payload;
      let document = state.documents.find((doc) => doc.type === type);
      if (!document) {
        document = {
          type,
          documentId: '',
          frontImage: '',
          backImage: '',
          verified: false,
        };
        state.documents.push(document);
      }
      if (side === 'front') {
        document.frontImage = url;
      } else {
        document.backImage = url;
      }
      if (expirationDate !== undefined) {
        document.expirationDate = expirationDate;
      }
    },
    setBiometricsCaptured: (state) => {
      state.hasBiometrics = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKYCStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchKYCStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.status = action.payload.status;
        state.citizenStatus = action.payload.citizenStatus || false;
        state.documents = action.payload.documents || [];
        state.hasBiometrics = action.payload.hasBiometrics || false;
        state.submittedAt = action.payload.submittedAt;
        state.approvedAt = action.payload.approvedAt;
        state.rejectionReason = action.payload.rejectionReason;
        state.expirationDate = action.payload.expirationDate || null;
        state.expired = action.payload.expired || false;
        state.requiresKYC = action.payload.requiresKYC !== undefined ? action.payload.requiresKYC : true;
      })
      .addCase(fetchKYCStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch KYC status';
      })
      .addCase(submitKYC.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitKYC.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.status = action.payload.status;
        state.submittedAt = action.payload.submittedAt;
        state.documents = action.payload.documents || [];
      })
      .addCase(submitKYC.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.error.message || 'KYC submission failed';
      });
  },
});

export const { updateDocument, setBiometricsCaptured } = kycSlice.actions;
export default kycSlice.reducer;

